import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { inject, type ModuleWithProviders, NgModule, Optional, provideAppInitializer, SkipSelf } from '@angular/core';
import { Router } from '@angular/router';

import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
} from '@azure/msal-angular';
import { InteractionType } from '@azure/msal-browser';
import type { IEnvironmentModel } from 'commons';
import { getMsalGuardConfig, getMsalInstanceConfig, getMsalInterceptorConfig } from './auth-config';

const AUTH_REDIRECT_COUNT_KEY = 'dtis_auth_redirect_count';
const AUTH_REDIRECT_TS_KEY = 'dtis_auth_redirect_ts';
/** Max consecutive failed redirect attempts within the time window before breaking the loop. */
const MAX_AUTH_REDIRECTS = 3;
/** Time window in ms — loops occur in seconds, so 30 s catches real loops without affecting normal use. */
const AUTH_REDIRECT_WINDOW_MS = 30_000;

function isRedirectLooping(): boolean {
  const now = Date.now();
  const lastTs = Number.parseInt(sessionStorage.getItem(AUTH_REDIRECT_TS_KEY) ?? '0', 10);
  const count = Number.parseInt(sessionStorage.getItem(AUTH_REDIRECT_COUNT_KEY) ?? '0', 10);

  if (now - lastTs > AUTH_REDIRECT_WINDOW_MS) {
    sessionStorage.setItem(AUTH_REDIRECT_COUNT_KEY, '1');
    sessionStorage.setItem(AUTH_REDIRECT_TS_KEY, String(now));
    return false;
  }

  const next = count + 1;
  sessionStorage.setItem(AUTH_REDIRECT_COUNT_KEY, String(next));
  return next > MAX_AUTH_REDIRECTS;
}

function clearRedirectLoopState(): void {
  sessionStorage.removeItem(AUTH_REDIRECT_COUNT_KEY);
  sessionStorage.removeItem(AUTH_REDIRECT_TS_KEY);
}

export function initializeApp(msalService: MsalService, router: Router) {
  return () =>
    msalService.instance.initialize().then(async () => {
      try {
        const result = await msalService.instance.handleRedirectPromise();
        if (result?.account) {
          msalService.instance.setActiveAccount(result.account);
          clearRedirectLoopState();
        } else if (isRedirectLooping()) {
          // No account and no error, but we've been here too many times: break the loop.
          clearRedirectLoopState();
          await router.navigate(['/forbidden']);
        }
      } catch (error) {
        // SSO failed — Microsoft redirected back with an error in the URL hash.
        // Navigate to the forbidden route immediately to break the infinite redirect loop.
        console.error('Error during redirect login:', error);
        clearRedirectLoopState();
        await router.navigate(['/forbidden']);
      }
    });
}

@NgModule({
  imports: [MsalModule],
  providers: [
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    provideAppInitializer(() => {
      const initializerFn = initializeApp(inject(MsalService), inject(Router));
      return initializerFn();
    }),
  ],
  bootstrap: [MsalRedirectComponent],
})
export class SharedAzureAuthModule {
  public static forRoot(environment: IEnvironmentModel): ModuleWithProviders<SharedAzureAuthModule> {
    return {
      ngModule: SharedAzureAuthModule,
      providers: [
        {
          provide: HTTP_INTERCEPTORS,
          useClass: MsalInterceptor,
          multi: true,
        },
        {
          provide: MSAL_INSTANCE,
          useValue: getMsalInstanceConfig({
            clientId: environment.clientId,
            tenantId: environment.tenantId,
            redirectUri: environment.redirectUri ?? globalThis.location.origin,
            postLogoutRedirectUri: environment.postLogoutRedirectUri ?? globalThis.location.origin,
            loggingEnabled: false,
            loginUrl: environment.loginUrl,
          }),
        },
        {
          provide: MSAL_INTERCEPTOR_CONFIG,
          useValue: getMsalInterceptorConfig({
            interactionType: InteractionType.Redirect,
            protectedResources: [
              {
                route: environment.e2e ? '' : '/api/*',
                scopes: environment.apiScope,
              },
            ],
          }),
        },
        {
          provide: MSAL_GUARD_CONFIG,
          useValue: getMsalGuardConfig({
            interactionType: InteractionType.Redirect,
            loginFailedRoute: '/forbidden',
            scopes: environment.appScope,
          }),
        },
      ],
    };
  }

  public constructor(@Optional() @SkipSelf() parentModule: SharedAzureAuthModule) {
    if (parentModule) {
      throw new Error('SharedAzureAuthModule is already loaded. Import it in the AppModule only');
    }
  }
}
